import { Dbg, Util } from "../util.js";
import { FormattingUtils } from './formattingutils.js';

/**
 * Classes for reading/writing parameters at
 * fixed offsets in a sysex file. Supports
 * different data types (numbers, strings, ...)
 * and various formats including less-than-byte
 * data at bit-offsets, and data range validation.
 */


 /** ASCII-string parameter */
 export class AsciiParam
 {
   /**
    * @param {number} offset Parameter offset (in bytes)
    * @param {string} name Parameter name
    * @param {number} length Fixed string length (in bytes)
    */
   constructor(offset, name, length) {
     this.offset = offset;
     this.name = name;
     this.length = length;
   }

   /** Checks that the parameter value is valid.
    * @param {value} string Parameter value to check.
    * @return {object} Validation result, containing:
    *  value - the corrected value (if applicable) (string)
    *  warning - warning message (string); undefined if value is valid.
    */
   validateValue(value) {
     let warning = undefined;
     if (value.length > this.length) {
       value = value.substring(0, this.length);
       warning = 'String is too long.';
     }
     if (FormattingUtils.hasNonPrintableChars(value)) {
       value = FormattingUtils.replaceNonPrintableChars(value);
       warning = 'String contains non-printable characters';
     }
     return {value, warning};
   }

   /** Gets the parameter value from rawData (no validation).
    * @param {Uint8Array} rawData Raw sysex data.
    * @return {string} The value from the raw sysex data.
    */
   getValue(rawData) {
     let str = '';
     for (var i = 0; i < this.length; i++) {
       let val = rawData[this.offset+i];
       str += String.fromCharCode(val);
     }
     str = str.trimRight(); // trim trailing whitespace
     return str;
   }

   /** Updates rawData with the specified value (no validation).
    * @param {Uint8Array} rawData Raw sysex data.
    * @param {string} value New parameter value.
    */
   setValue(rawData, value) {
     for (var i = 0; i < this.length; i++) {
       let char = 0x20;
       if (i < value.length) {
         char = value.charCodeAt(i);
       }
       rawData[this.offset+i] = char;
     }
   }
 }


 /** Numeric parameter */
 export class NumParam
 {
   /** Defines a numeric parameter.
    * @param {number} offset Byte-offset in raw data.
    * @param {string} name Parameter name
    * @param {number} min Minimum valid parameter value.
    * @param {number} max Maximum valid parameter value.
    * @param {number} bitCount (optional) Number of bits for parameter.
    * @param {number} bitOffset (optional) Parameter's bit offset (from right).
    * See e.g. the Yamaha DX7 packed voice data format for examples
    * of packed data with more than one parameter per MIDI-byte.
    */
   constructor(offset, name, min, max, bitCount = 7, bitOffset = 0) {
     this.offset = offset;
     this.name = name;
     this.min = min;
     this.max = max;
     this.bitCount = bitCount;
     this.bitOffset = bitOffset;
   }

   /** Checks that the parameter value is valid.
    * @param {value} number Parameter value to check.
    * @return {object} Validation result, containing:
    *  value - the corrected value (if applicable) (number)
    *  warning - warning message (string); undefined if value is valid.
    */
   validateValue(value) {
     let warning = undefined;
     if (value < this.min) {
       warning = 'Parameter ' + this.name +
        ': Value ' + value + ' is too small.';
       value = this.min;
     }
     if (value > this.max) {
       warning = 'Parameter ' + this.name +
        ': Value ' + value + ' is too large.';
       value = this.max;
     }
     return {value: value, warning: warning};
   }

   /** Gets the parameter value from rawData (no validation).
    * @param {Uint8Array} rawData Raw sysex data.
    * @return {number} The value from the raw sysex data.
    */
   getValue(rawData) {
     let mask = (1 << this.bitCount) - 1;
     let value  = (rawData[this.offset] >> this.bitOffset) & mask;
     return value;
   }

   /** Updates rawData with the specified value (no validation).
    * @param {Uint8Array} rawData Raw sysex data.
    * @param {number} value New parameter value.
    */
   setValue(rawData, value) {
     let srcMask = (1 << this.bitCount) - 1;
     let dstMask = 0xFF - (srcMask << this.bitOffset);
     value = (value << this.bitOffset) & srcMask;
     rawData[this.offset] &= dstMask;
     rawData[this.offset] |= value;
   }
 }



// WEBPACK FOOTER //
// ./src/parsing_formatting/parameters.js